import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  usePage,
  useVibesites,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
// Copy-source override of the company Header. Nav metadata is merged
// homestead → company → this property so inherited labels/order stay in
// lockstep, including company additions such as FAQ.
import parentPagesData from '@vibesites/theme-theme-homestead/pages.json';
import companyPagesData from '@vibesites-apartment-management-consultants/theme-theme-homestead-milburn/pages.json';
import childPagesData from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  MultihubLogoMonoWhite,
  repliWhiteLogoUrl,
} from '@vibesites/assets';
import { hasValue } from '../lib/petPolicyList';
// Not a registry slot — nested helper. Tokens stay on Header's contentSchema.
import { CommunityInfoModal } from './CommunityInfoModal';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (indented row inside the slide-in drawer). */
  parent?: string;
}

/**
 * Effective page map used to build the navigation. Homestead pages provide
 * the canonical labels, positions, and visibility; the company theme is
 * overlaid next, then this property's pages.json — mirroring the deploy
 * pipeline's base → company → property merge for the fields the nav reads.
 */
const NAV_PAGES: Record<string, PageEntry> = (() => {
  const merged: Record<string, Partial<PageEntry>> = {};
  for (const [alias, page] of Object.entries(parentPagesData as Record<string, Partial<PageEntry>>)) {
    merged[alias] = { ...page };
  }
  for (const [alias, page] of Object.entries(companyPagesData as Record<string, Partial<PageEntry>>)) {
    merged[alias] = { ...(merged[alias] ?? {}), ...page };
  }
  for (const [alias, page] of Object.entries(childPagesData as Record<string, Partial<PageEntry>>)) {
    merged[alias] = { ...(merged[alias] ?? {}), ...page };
  }
  return merged as Record<string, PageEntry>;
})();

/**
 * "Community Info" is a nav entry that is NOT itself a page — clicking it opens
 * a modal pop-up listing these existing pages. Order here is the order shown in
 * the pop-up. Each alias is resolved against NAV_PAGES so labels/paths stay in
 * lockstep with the page metadata; entries that are hidden (or, for
 * virtual-tour, absent for this property) are dropped from the pop-up.
 */
const COMMUNITY_INFO_ALIASES = ['neighborhood', 'amenities', 'pet-policy'];

/** Page aliases that should never appear in the main navigation */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  // FAQ is rendered after Resident Access when the page is enabled for the
  // property (theme default is hidden; property pages.json can turn it on).
  'faq',
  'schedule-a-tour',
  'virtual-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** pages.json position — used to interleave the non-page "Community Info"
      entry at the right spot in the top nav. */
  position?: number;
  /** Sub-nav items rendered as indented rows inside the drawer. Populated
      when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/** A slot in the desktop top nav: either a real page link or the non-page
    "Community Info" trigger. Sorted together by `position` so Community Info
    lands between Gallery and Contact Us. */
type TopNavEntry =
  | { kind: 'page'; link: NavLink; position: number }
  | { kind: 'community'; position: number };

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry. Orphaned children (parent missing or
 * hidden) fall back to the top level so a page is never silently dropped.
 *
 * `hasInteractiveMap` controls the dynamic visibility of the Interactive Map
 * page — shown when ApartmentSync is fully configured, or when an Engrain
 * Sightmap URL/ID is present on the property.
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveMap: boolean): NavLink[] {
  const eligible = Object.entries(NAV_PAGES as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      // Pages surfaced inside the "Community Info" dropdown are not repeated as
      // stand-alone top-level nav items — unless they declare a `parent`, in
      // which case they still nest under that parent's dropdown (e.g. Virtual
      // Tour appears both under Floor Plans and in Community Info).
      if (COMMUNITY_INFO_ALIASES.includes(alias) && !page.parent) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveMap) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path, position: page.position });
  }

  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

/** Add a leading "/" + normalize a trailing slash so two paths from different
    sources (pages.json `path`, runtime `page.path`, window.location.pathname)
    compare equal regardless of trailing-slash convention. */
function normalizePath(p: string | undefined | null): string {
  if (!p) return '';
  let s = p.trim();
  if (!s.startsWith('/')) s = `/${s}`;
  if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
  return s;
}

/**
 * Dropdown / drawer-subnav rows for a page that has children. The parent page
 * is listed first so the landing page is reachable from within its own menu —
 * desktop top-nav and the hamburger drawer must stay identical.
 */
function pageDropdownLinks(link: NavLink): NavLink[] {
  return [
    { label: link.label, href: link.href },
    ...(link.children ?? []),
  ];
}

// ============================================================================
// Office Hours formatting (mirrors Footer.tsx)
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_FULL_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

/**
 * Every day of the week with its hours (or "Closed"), listed individually —
 * used by the Community Info modal which shows Monday…Sunday as separate rows
 * (unlike the drawer's grouped "Mon - Fri" summary).
 */
function listOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) byDay.set(key, entry);
  }
  return DAY_ORDER.map((key) => {
    const entry = byDay.get(key);
    const isClosed =
      !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    return {
      label: DAY_FULL_LABELS[key],
      hours: isClosed
        ? 'Closed'
        : `${formatTime(entry!.open_time || '')} - ${formatTime(entry!.close_time || '')}`,
    };
  });
}

interface GroupedHours {
  label: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/apply-now',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule a Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/schedule-a-tour',
      suggestedFields: ['property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Address row inside the slide-in drawer ("Visit Us"). The URL has no
// defaultField — it defaults to usePropertyAddressUrl() at render time
// (mobile-safe Google Maps URL built from place / business / full address /
// lat-lon fallbacks). An explicit editor override wins.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icons
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6.62 10.79a15.15 15.15 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.4 21 3 13.6 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.25 1.02l-2.2 2.19z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14c-.326-.043-1.557-.14-2.857-.14C11.928 2 10 3.657 10 6.7v2.8H7v4h3V22h4v-8.5z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.2c3.2 0 3.6 0 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s0 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58 0-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.58 2.2 15.2 2.2 12s0-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2zm0 1.8c-3.15 0-3.5 0-4.74.07-.89.04-1.37.19-1.69.31-.43.17-.73.36-1.05.68-.32.32-.51.62-.68 1.05-.12.32-.27.8-.31 1.69C3.26 8.5 3.25 8.85 3.25 12s0 3.5.07 4.74c.04.89.19 1.37.31 1.69.17.43.36.73.68 1.05.32.32.62.51 1.05.68.32.12.8.27 1.69.31 1.24.07 1.59.07 4.74.07s3.5 0 4.74-.07c.89-.04 1.37-.19 1.69-.31.43-.17.73-.36 1.05-.68.32-.32.51-.62.68-1.05.12-.32.27-.8.31-1.69.07-1.24.07-1.59.07-4.74s0-3.5-.07-4.74c-.04-.89-.19-1.37-.31-1.69a2.8 2.8 0 0 0-.68-1.05 2.8 2.8 0 0 0-1.05-.68c-.32-.12-.8-.27-1.69-.31C15.5 4 15.15 4 12 4zm0 3.06a4.94 4.94 0 1 1 0 9.88 4.94 4.94 0 0 1 0-9.88zm0 1.8a3.14 3.14 0 1 0 0 6.28 3.14 3.14 0 0 0 0-6.28zm5.15-3.24a1.15 1.15 0 1 1 0 2.3 1.15 1.15 0 0 1 0-2.3z" />
    </svg>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 11v2.9h4.05c-.17 1.06-.68 1.96-1.45 2.57v2.13h2.35C18.7 17.33 19.5 14.87 19.5 12c0-.66-.06-1.29-.17-1.9H12V11z" />
      <path d="M12 20c1.96 0 3.6-.65 4.8-1.76l-2.35-2.13c-.65.44-1.49.7-2.45.7-1.88 0-3.48-1.27-4.05-2.98H5.52v2.2A8 8 0 0 0 12 20z" />
      <path d="M7.95 13.83A4.8 4.8 0 0 1 7.7 12c0-.64.11-1.26.25-1.83v-2.2H5.52A8 8 0 0 0 4 12c0 1.29.31 2.51.85 3.6l2.29-1.77z" />
      <path d="M12 7.19c1.06 0 2.02.37 2.77 1.08l2.08-2.08C15.6 5.03 13.96 4 12 4a8 8 0 0 0-6.48 3.4l2.43 2.2C8.52 8.47 10.12 7.19 12 7.19z" />
    </svg>
  );
}

function CaretIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6" />
    </svg>
  );
}

/** Drawer label with a bold overlay that snaps on hover and slides away to
 *  the left on leave. An invisible bold copy sizes the box so the wider
 *  bold glyphs aren't clipped on the right. */
function DrawerNavLabel({ children }: { children: string }) {
  return (
    <span className="drawer-nav-label" data-text={children}>
      <span className="drawer-nav-label-measure" aria-hidden="true">
        {children}
      </span>
      <span className="drawer-nav-label-face">{children}</span>
    </span>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// Off-canvas drawer width, published as a CSS variable so the panel's own
// width and the distance the page (document.body) is pushed to the right stay
// in lockstep — including across a breakpoint change while the drawer is open.
// Tablet uses a narrower rail so a useful slice of the page stays visible.
const DRAWER_WIDTH_VAR = '--vibe-drawer-width';
const DRAWER_WIDTH = `var(${DRAWER_WIDTH_VAR})`;
const TABLET_DRAWER_WIDTH = 'min(86vw, 26rem)';
const DESKTOP_DRAWER_WIDTH = 'min(86vw, 34rem)';

/** Viewports where the drawer is the off-canvas left rail that pushes the page
    aside (tablet and up). Below this it is the full-width top slide-down. */
const SIDE_DRAWER_MQ = '(min-width: 768px)';

/** Side-drawer viewports driven by a real pointer: sub-navs expand on hover, so
    parent labels stay plain navigation links. Touch tablets get the left rail
    too but fail this query, so they keep the tap-to-expand accordion (sticky
    :hover would pin their menus open). */
const HOVER_NAV_MQ = '(min-width: 768px) and (hover: hover) and (pointer: fine)';

/** Page whose sub-nav is expanded the moment a tap-driven drawer opens. */
const DEFAULT_EXPANDED_ALIAS = 'floor-plans';

/**
 * True where the drawer's accordion rows rely on taps rather than hover — the
 * mobile top slide-down and touch tablets. There a parent label toggles its
 * sub-nav instead of navigating.
 */
function usesTapNav(): boolean {
  return typeof window !== 'undefined' && !window.matchMedia(HOVER_NAV_MQ).matches;
}

/**
 * Renders its children into a fresh element appended to <html> (outside
 * <body>). Wherever the drawer is the side rail it must live outside <body>
 * because we translate <body> to push the page — a drawer inside <body> would
 * move with it. On mobile it also keeps the full-screen overlay above page
 * stacking contexts.
 * Renders nothing until mounted on the client, so it never participates in SSG
 * markup or hydration (the drawer is closed/hidden at first paint anyway).
 */
function HtmlPortal({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const el = document.createElement('div');
    document.documentElement.appendChild(el);
    setHost(el);
    return () => { document.documentElement.removeChild(el); };
  }, []);
  return host ? createPortal(children, host) : null;
}

// ============================================================================
// Main Header Component
// ----------------------------------------------------------------------------
// STRUCTURE: A simple white, pinned (fixed) header bar.
//   Desktop (lg+):  [hamburger + logo]   [page links centered in the bar]
//   Mobile (<lg):   [hamburger]          [logo centered]          [phone]
// On desktop the primary navigation is absolutely centered on the full bar
// width so the logo on the left does not shift the page links off-center.
// Pages with sub-pages open a hover/focus dropdown. The always-visible
// hamburger opens the nav drawer:
//   Tablet + desktop (md+): narrow dark panel that slides in from the LEFT and
//     pushes the page aside (a narrower rail on tablet than on desktop).
//   Phone (<md): full-width dark overlay that slides in from the TOP.
// The drawer stacks: nav links (Floor Plans, Gallery, Community Info, Contact
// Us — items with sub-pages expand inline via a caret), the address ("Visit
// Us"), the phone ("Let's Talk") with social icons beneath it, and — on
// desktop only — the Managed-By / Multihub / Repli credits. The narrower
// tablet rail drops those credits. In the phone overlay, social icons pin
// to the drawer foot instead.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  // Nav bar height only (excludes offer-banner padding). Spacer adds
  // --offer-banner-height via CSS so it stays in lockstep with the fixed
  // header when the banner opens/closes — measuring the full header via
  // ResizeObserver raced the CSS variable and caused intermittent gap /
  // title-under-header.
  const [navHeight, setNavHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  const headerBarRef = useRef<HTMLDivElement>(null);

  // Current page path — drives the active-link underline in the drawer.
  const pageCtx = usePage();
  const currentPath = useMemo(
    () =>
      normalizePath(
        typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path,
      ),
    [pageCtx?.path],
  );

  // The drawer is a fixed dark panel, so its text and every credit mark
  // (Managed-By / Multihub / Repli) always use the white/light-on-dark variant.

  // Which top-level drawer nav item (if any) has its sub-pages expanded. A
  // single-open accordion: opening one collapses the others. Reset whenever the
  // drawer itself closes so it reopens clean.
  const [expandedNav, setExpandedNav] = useState<string | null>(null);
  const toggleNav = useCallback(
    (key: string) => setExpandedNav((prev) => (prev === key ? null : key)),
    [],
  );

  // Desktop top-nav dropdowns open purely on :hover / :focus-within. After a
  // row inside one is clicked the pointer (and focus) usually stays on the
  // item, so the panel would linger over the page that was just navigated to.
  // Clicking marks that dropdown dismissed — CSS force-hides it until the
  // pointer leaves the item, which re-arms the normal hover behaviour.
  const [dismissedDropdown, setDismissedDropdown] = useState<string | null>(null);
  const dismissDropdown = useCallback((key: string) => {
    setDismissedDropdown(key);
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  }, []);
  const releaseDropdown = useCallback(
    (key: string) => setDismissedDropdown((prev) => (prev === key ? null : prev)),
    [],
  );

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — in nav when ApartmentSync is fully configured, Engrain
  // Sightmap URL/ID is set, or the editor "show if exists" token has a value.
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as string | null | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as string | null | undefined;
  const hasApartmentSync =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());
  const hasEngrainSightmap = !!(
    String(propertyForLeasing?.custom_engrain_sightmap_url || '').trim() ||
    String(propertyForLeasing?.custom_engrain_sitemap_url || '').trim() ||
    String(propertyForLeasing?.custom_engrain_sightmap_id || '').trim() ||
    String(propertyForLeasing?.custom_engrain_sitemap_id || '').trim()
  );

  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists', {
    defaultField: 'custom_engrain_sightmap_url',
  });
  const hasInteractiveMapOverride = !!(interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim());

  const hasInteractiveMap = hasApartmentSync || hasEngrainSightmap || hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveMap),
    [hasVirtualTours, hasInteractiveMap],
  );

  // Floor Plans starts expanded in the mobile drawer so its sub-pages are
  // visible without a tap. Resolved from the built nav links, so it no-ops for
  // properties where Floor Plans ends up with no visible sub-pages.
  const defaultExpandedNav = useMemo(() => {
    const path = NAV_PAGES[DEFAULT_EXPANDED_ALIAS]?.path;
    if (!path) return null;
    const target = normalizePath(path);
    const match = navLinks.find((link) => normalizePath(link.href) === target);
    return match?.children?.length ? match.href : null;
  }, [navLinks]);

  // "Community Info" pop-up — a non-page nav entry that opens a modal listing a
  // curated set of existing pages. Virtual Tour is included only when the
  // property actually has tours (same gate the primary nav uses).
  const [communityInfoOpen, setCommunityInfoOpen] = useState(false);
  const communityInfoLinks = useMemo<NavLink[]>(
    () =>
      COMMUNITY_INFO_ALIASES.map((alias) => {
        const page = NAV_PAGES[alias];
        if (!page || page.visible === false) return null;
        if (alias === 'virtual-tour' && !hasVirtualTours) return null;
        return { label: page.name, href: page.path };
      }).filter((l): l is NavLink => l !== null),
    [hasVirtualTours],
  );
  const closeCommunityInfo = useCallback(() => setCommunityInfoOpen(false), []);

  // Interleave the non-page "Community Info" trigger into the top nav by
  // position: just after Gallery, so it sits between Gallery and Contact Us.
  const topNavEntries = useMemo<TopNavEntry[]>(() => {
    const entries: TopNavEntry[] = navLinks.map((link) => ({
      kind: 'page',
      link,
      position: link.position ?? 0,
    }));
    if (communityInfoLinks.length > 0) {
      const galleryPos = NAV_PAGES['gallery']?.position ?? 5;
      entries.push({ kind: 'community', position: galleryPos + 0.5 });
    }
    return entries.sort((a, b) => a.position - b.position);
  }, [navLinks, communityInfoLinks]);

  // Content tokens
  const phone = useContentToken('header.phone');
  const feeGuideCtaLabel = useContentToken('header.fee_guide_cta_label');

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const propertyName = usePropertyField('property_name') as string | null;

  // The bar sits on white → prefer the dark/mono-black logo, falling back to
  // whatever the editor picked.
  const barLogoImage = monoBlackLogo || logoRaw.image;
  const phoneDigits = (phone || '').replace(/[^+\d]/g, '');

  // Management-company mark shown in the drawer's "Professionally Managed By"
  // credit. Drawer is dark → prefer the white/mono-white company logo, falling
  // back to the color company logo then the property's secondary logo.
  const companyMonoWhiteLogo = usePropertyField('property_company_mono_white_logo');
  const companyLogo = usePropertyField('property_company_logo');
  const secondaryLogo = usePropertyField('property_secondary_logo');
  const companyName = usePropertyField('property_company_name');
  const companyUrl = usePropertyField('property_company_url');
  const managedByImage = companyMonoWhiteLogo || companyLogo || secondaryLogo;
  // If the property has no dedicated mono-white company logo, synthesize one by
  // forcing whatever fallback logo (color company logo / secondary logo) to
  // pure white so it stays legible on the dark drawer. `brightness(0)` flattens
  // every pixel to black, then `invert(1)` flips those opaque pixels to white
  // while leaving transparent areas transparent — a mono-white render of any
  // source mark. A real mono-white asset is used as-is (no filter).
  const managedByNeedsWhitening = !companyMonoWhiteLogo && !!managedByImage;

  // Social channels shown as icons below the phone (desktop) / at the foot of
  // the drawer (mobile) — each rendered only when the property has that URL set.
  const facebookUrl = usePropertyField('property_facebook_url');
  const instagramUrl = usePropertyField('property_instagram_url');
  const googleUrl = usePropertyField('property_google_business_url');
  const hasSocialLinks = !!(facebookUrl || instagramUrl || googleUrl);
  const drawerSocialIcons = hasSocialLinks ? (
    <>
      {facebookUrl && (
        <a href={facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="drawer-social">
          <FacebookIcon className="h-full w-full" />
        </a>
      )}
      {instagramUrl && (
        <a href={instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="drawer-social">
          <InstagramIcon className="h-full w-full" />
        </a>
      )}
      {googleUrl && (
        <a href={googleUrl} target="_blank" rel="noopener noreferrer" aria-label="Google" className="drawer-social">
          <GoogleIcon className="h-full w-full" />
        </a>
      )}
    </>
  ) : null;

  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();
  // Resident Portal URL — used when a slot url is blank.
  const mhResidentPortalUrl =
    (usePropertyField('property_resident_portal_url') as string | null) || '';
  const residentAccessHref = residentAccessLink.url || mhResidentPortalUrl;

  // FAQ sits after Resident Access (Duda secondary-nav order). Theme pages.json
  // keeps FAQ hidden; property pages.json can enable it. Header's static
  // NAV_PAGES won't see that override, so we also accept runtime `pagePaths`
  // (merged visible + indexable pages from preview/SSG).
  const { pagePaths } = useVibesites();
  const faqPage = NAV_PAGES.faq;
  const faqNavLink = useMemo(() => {
    if (!faqPage?.path) return null;
    const path = normalizePath(faqPage.path);
    const enabledInTheme = faqPage.visible !== false;
    const enabledAtRuntime = (pagePaths ?? []).some((p) => normalizePath(p) === path);
    if (!enabledInTheme && !enabledAtRuntime) return null;
    return { label: faqPage.name || 'FAQ', href: faqPage.path };
  }, [pagePaths]);

  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', { collection: 'office_hours' });
  // Community Info modal shows every day individually (Mon…Sun).
  const modalHours = officeHours && officeHours.length > 0 ? listOfficeHours(officeHours) : [];

  // Google Fonts — heading + body (Josefin Sans), accent (Dancing Script), and
  // Josefin Slab (the slab/western display face used by content section titles
  // like PropertyOverview). Loaded globally from the Header so every page
  // renders the theme's typography. (The hero titles stay on Josefin Sans.)
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="homestead-fonts"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@300;400;500;600;700&family=Josefin+Slab:wght@300;400;500;600;700&family=Dancing+Script:wght@500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'homestead-fonts');
    document.head.appendChild(link);
  }, []);

  // Measure the nav bar only. Offer-banner clearance is applied in CSS on the
  // spacer (see .header-spacer) so it tracks --offer-banner-height instantly.
  const updateNavHeight = useCallback(() => {
    if (headerBarRef.current) setNavHeight(headerBarRef.current.offsetHeight);
  }, []);

  useEffect(() => {
    updateNavHeight();
    const el = headerBarRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateNavHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateNavHeight]);

  // Lock page scroll while the drawer or the Community Info modal is open.
  // With the side drawer the viewport scroller is <html> (and <body> is
  // translated while it is open), so overflow must be set on both.
  useEffect(() => {
    const locked = menuOpen || communityInfoOpen;
    document.documentElement.style.overflowY = locked ? 'hidden' : '';
    document.body.style.overflow = locked ? 'hidden' : '';
    return () => {
      document.documentElement.style.overflowY = '';
      document.body.style.overflow = '';
    };
  }, [menuOpen, communityInfoOpen]);

  // Tablet + desktop: push the whole page right by the drawer's width so the
  // left drawer slides in beside the content. The drawer lives in an
  // <html>-level portal, so translating <body> moves everything except the
  // drawer. Phone: leave the page in place — the full-width top overlay covers
  // it. `vibe-nav-open` also collapses the SpecialOfferBanner while open.
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const mq = window.matchMedia(SIDE_DRAWER_MQ);

    const apply = () => {
      const sidePush = menuOpen && mq.matches;
      html.classList.toggle('vibe-nav-open', menuOpen);
      body.style.transition = 'transform 0.3s ease';
      body.style.transform = sidePush ? `translateX(${DRAWER_WIDTH})` : '';
      html.style.overflowX = sidePush ? 'hidden' : '';
    };

    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      body.style.transform = '';
      body.style.transition = '';
      html.style.overflowX = '';
      html.classList.remove('vibe-nav-open');
    };
  }, [menuOpen]);

  // Opening a tap-driven drawer pre-expands the Floor Plans sub-nav. Where the
  // rail expands on hover it opens with every group collapsed.
  useEffect(() => {
    if (!menuOpen || !defaultExpandedNav || !usesTapNav()) return;
    setExpandedNav(defaultExpandedNav);
  }, [menuOpen, defaultExpandedNav]);

  useEffect(() => {
    if (!menuOpen && !communityInfoOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      setCommunityInfoOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen, communityInfoOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
    setExpandedNav(null);
  };

  return (
    <>
      {/* ============================================================== */}
      {/* Pinned white header bar                                        */}
      {/* ============================================================== */}
      <header
        ref={headerRef}
        className={`fixed left-0 right-0 top-0 w-full transition-[background-color,box-shadow] duration-300${menuOpen ? ' z-[70] is-menu-open' : ' z-50 bg-[var(--vibe-color-background)] header-elevated'}`}
        style={{ paddingTop: 'var(--offer-banner-height, 0px)' }}
      >
        {/* Editor-only control. Holds the name of the property field whose
            presence exposes the Interactive Map page in the nav. Rendered
            visually hidden because it's configuration, not display copy. */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Expose the Interactive Map page when this field has a value. Defaults to custom_engrain_sightmap_url."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>
        {/* Editor-only — label is rendered inside CommunityInfoModal; kept here
            so the Header schema token stays inline-selectable. */}
        <EditableToken
          path="header.fee_guide_cta_label"
          type="text"
          label="Community Info: Apply Now CTA Label"
          as="span"
          className="sr-only"
        >
          {feeGuideCtaLabel || ''}
        </EditableToken>

        <div ref={headerBarRef} className="header-bar px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center h-20 lg:h-24 gap-4">
            {/* Left cluster — hamburger + logo. On lg+ these stay grouped
                together on the left (matching the desktop reference). On mobile
                the logo is pulled out of flow and pinned to the horizontal
                midpoint of the bar (positioned against this row — not this
                cluster — so it truly centers), so the cluster effectively
                contains only the hamburger there. The hamburger stays anchored
                to the left at every breakpoint so the drawer is always
                available. `z-10` keeps the cluster above the absolutely
                centered desktop nav if the two ever collide. */}
            <div className="z-10 flex items-center gap-3 flex-shrink-0">
              {/* Menu toggle — a hamburger that flips to a boxed "X" while the
                  drawer is open. On mobile the header rises above the top
                  overlay (z-70). On desktop the page is pushed right by the
                  drawer width, so the X sits just outside the panel's edge. */}
              <button
                onClick={() => setMenuOpen((open) => !open)}
                className={`menu-toggle relative flex h-10 w-10 flex-shrink-0 items-center justify-center ${menuOpen ? 'is-open' : ''}`}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-haspopup="true"
              >
                {menuOpen ? (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                  </svg>
                ) : (
                  <span className="flex flex-col items-center justify-center gap-[5px]">
                    <span className="block w-6 h-0.5 bg-current" />
                    <span className="block w-6 h-0.5 bg-current" />
                    <span className="block w-6 h-0.5 bg-current" />
                  </span>
                )}
              </button>

              {/* Logo — on mobile it's absolutely centered in the header bar
                  (out of flow, so it sits at the horizontal midpoint of the
                  row regardless of the hamburger / phone widths). On lg+ it
                  reverts to static positioning and sits in-flow beside the
                  hamburger, matching the desktop reference. */}
              <EditableSlotGroup
                groupPath="header.logo"
                definition={logoSlotDef}
                className="header-logo-slot absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 lg:static lg:translate-x-0 lg:translate-y-0 flex-shrink-0 max-w-[min(16rem,calc(100%-8.5rem))] lg:max-w-none"
              >
                <a href="/" className="block">
                  {barLogoImage ? (
                    <VibesitesImage
                      url={barLogoImage}
                      alt={logoRaw.alt || propertyName || 'Community Logo'}
                      sizes="240px"
                      className="h-14 sm:h-16 lg:h-14 w-auto max-w-full object-contain"
                    />
                  ) : (
                    <div className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold tracking-wide text-[var(--vibe-color-text)] truncate">
                      {propertyName || 'Community'}
                    </div>
                  )}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Phone-only tap-to-call icon, sat on the right of the bar. Hidden
                from md up — on tablet and desktop the number lives in the
                drawer — and while the menu is open (see .header-phone-icon
                rule below). The centered logo is out of flow, so it stays on
                the bar's midpoint with or without this icon. */}
            <a
              href={`tel:${phoneDigits}`}
              className="header-phone-icon dniphone dniphonehref relative z-10 ml-auto flex h-12 w-12 flex-shrink-0 items-center justify-center text-[var(--vibe-color-text)] md:hidden"
              aria-label={formatPhone(phone) ? `Call ${formatPhone(phone)}` : 'Call us'}
            >
              <PhoneIcon className="h-7 w-7" />
            </a>

            {/* Primary navigation — inline horizontal row of pages (desktop only;
                below lg every link lives in the drawer menu). Pages with
                sub-pages open a hover/focus dropdown. Absolutely centered on
                the full bar (`left-1/2` + `-translate-x-1/2`) so the page
                links stay on the true midpoint even though the logo sits on
                the left. */}
            <nav
              className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center gap-1"
              role="navigation"
              aria-label="Primary navigation"
            >
              {topNavEntries.map((entry) => {
                // Non-page "Community Info" trigger. Hover reveals a dropdown of
                // the curated community pages (Virtual Tours, Neighborhood,
                // Amenities, Pet Policy); clicking the label opens the info
                // pop-up (contact, hours, pet policy).
                if (entry.kind === 'community') {
                  return (
                    <div
                      key="community-info"
                      className={`top-nav-item ${dismissedDropdown === 'community-info' ? 'is-dismissed' : ''}`}
                      onMouseLeave={() => releaseDropdown('community-info')}
                    >
                      <button
                        type="button"
                        onClick={() => { dismissDropdown('community-info'); setCommunityInfoOpen(true); }}
                        aria-haspopup="dialog"
                        aria-expanded={communityInfoOpen}
                        className="top-nav-link"
                      >
                        Community Info
                        <CaretIcon className="top-nav-caret w-3.5 h-3.5" />
                      </button>
                      <div className="top-nav-dropdown" role="menu">
                        {/* Opens the same "Community Info" pop-up the trigger
                            label does — a non-page entry surfaced inside the
                            dropdown so the hours/contact modal is reachable
                            without leaving the current page. */}
                        <button
                          type="button"
                          onClick={() => { dismissDropdown('community-info'); setCommunityInfoOpen(true); }}
                          role="menuitem"
                          aria-haspopup="dialog"
                          className="top-nav-dropdown-link top-nav-dropdown-button"
                        >
                          Hours &amp; Info
                        </button>
                        {communityInfoLinks.map((child) => {
                          const childActive = normalizePath(child.href) === currentPath;
                          return (
                            <a
                              key={child.href}
                              href={child.href}
                              onClick={() => dismissDropdown('community-info')}
                              role="menuitem"
                              aria-current={childActive ? 'page' : undefined}
                              className={`top-nav-dropdown-link ${childActive ? 'is-active' : ''}`}
                            >
                              {child.label}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                const link = entry.link;
                const active = normalizePath(link.href) === currentPath;
                const hasChildren = !!link.children && link.children.length > 0;
                if (hasChildren) {
                  return (
                    <div
                      key={link.href}
                      className={`top-nav-item ${dismissedDropdown === link.href ? 'is-dismissed' : ''}`}
                      onMouseLeave={() => releaseDropdown(link.href)}
                    >
                      <a
                        href={link.href}
                        onClick={() => dismissDropdown(link.href)}
                        aria-current={active ? 'page' : undefined}
                        className={`top-nav-link ${active ? 'is-active' : ''}`}
                      >
                        {link.label}
                        <CaretIcon className="top-nav-caret w-3.5 h-3.5" />
                      </a>
                      <div className="top-nav-dropdown" role="menu">
                        {pageDropdownLinks(link).map((item) => {
                          const itemActive = normalizePath(item.href) === currentPath;
                          return (
                            <a
                              key={item.href}
                              href={item.href}
                              onClick={() => dismissDropdown(link.href)}
                              role="menuitem"
                              aria-current={itemActive ? 'page' : undefined}
                              className={`top-nav-dropdown-link ${itemActive ? 'is-active' : ''}`}
                            >
                              {item.label}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    aria-current={active ? 'page' : undefined}
                    className={`top-nav-link ${active ? 'is-active' : ''}`}
                  >
                    {link.label}
                  </a>
                );
              })}
              {/* Schedule a Tour + Apply Now + Resident Access — plain page links in the primary nav */}
              <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                <a
                  href={scheduleTourLink.url || '/schedule-a-tour'}
                  target={scheduleTourLink._target || '_self'}
                  className="top-nav-link"
                >
                  {scheduleTourLink.label || 'Schedule a Tour'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  className="top-nav-link"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessHref || '#'}
                  target={residentAccessLink._target || '_blank'}
                  rel={residentAccessLink._target === '_self' ? undefined : 'noopener noreferrer'}
                  className="top-nav-link"
                >
                  {residentAccessLink.label || 'Resident Access'}
                </a>
              </EditableSlotGroup>
              {faqNavLink ? (
                <a
                  href={faqNavLink.href}
                  aria-current={normalizePath(faqNavLink.href) === currentPath ? 'page' : undefined}
                  className={`top-nav-link ${normalizePath(faqNavLink.href) === currentPath ? 'is-active' : ''}`}
                >
                  {faqNavLink.label}
                </a>
              ) : null}
            </nav>
          </div>
        </div>
      </header>

      {/* Layout spacer beneath the fixed bar. Height = nav + offer banner.
          Banner clearance uses the CSS variable (same source as the fixed
          header's paddingTop) so the two never get out of sync. */}
      <div
        aria-hidden="true"
        className="header-spacer"
        style={
          navHeight
            ? { height: `calc(${navHeight}px + var(--offer-banner-height, 0px))` }
            : undefined
        }
      />

      {/* ============================================================== */}
      {/* Drawer — tablet + desktop: slides in from the left (pushes page);
          phone: slides down from the top (portaled to <html>) */}
      {/* ============================================================== */}
      <HtmlPortal>
        <div
          className={`fixed inset-0 z-[60] transition-[visibility] duration-300 ${
            menuOpen ? 'visible' : 'invisible pointer-events-none'
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
        >
          {/* Backdrop — dims the page behind / beside the drawer. */}
          <div
            onClick={closeMenu}
            className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
              menuOpen ? 'opacity-100' : 'opacity-0'
            }`}
          />

          {/* Panel — phone: full-width, slides from top. Tablet + desktop:
              narrow left rail (DRAWER_WIDTH), slides from left. Close control
              stays in the header bar. */}
          <aside
            className={`drawer-panel absolute top-0 flex h-full flex-col overflow-y-auto shadow-2xl ${
              menuOpen ? 'is-open' : ''
            }`}
          >
          <div
            className="drawer-panel-inner relative flex min-h-full flex-col px-7 pb-7 sm:px-9 sm:pb-9"
            style={{
              // Phone top-drawer clears the raised header; the side rail resets
              // this via CSS.
              ['--drawer-pad-top' as string]: navHeight
                ? `calc(${navHeight}px + 0.5rem)`
                : '4rem',
            }}
          >
            {/* ---- Navigation ---- */}
            <nav
              className="flex flex-col items-center text-center md:mt-2 md:items-stretch md:text-left"
              role="navigation"
              aria-label="Main navigation"
            >
              {topNavEntries.map((entry) => {
                // Non-page "Community Info" — a caret-expandable row that
                // reveals the curated community pages plus the "Hours & Info"
                // pop-up trigger inline.
                if (entry.kind === 'community') {
                  if (communityInfoLinks.length === 0) return null;
                  const expanded = expandedNav === 'community-info';
                  // Sub-nav is always rendered so it can open on hover (CSS);
                  // the `is-open` class mirrors the click/touch toggle state.
                  // Where the drawer is tap-driven the label toggles the
                  // sub-page list just like the caret (the "Hours & Info" row
                  // inside opens the pop-up). With a hover pointer, where the
                  // list already expands on hover, the label keeps opening the
                  // Community Info pop-up directly — matching the primary nav's
                  // click behavior.
                  return (
                    <div key="community-info" className={`drawer-nav-group ${expanded ? 'is-open' : ''}`}>
                      <div className="drawer-nav-row">
                        <button
                          type="button"
                          onClick={() => {
                            if (usesTapNav()) {
                              toggleNav('community-info');
                              return;
                            }
                            closeMenu();
                            setCommunityInfoOpen(true);
                          }}
                          aria-haspopup="dialog"
                          className="drawer-nav-link font-heading"
                        >
                          <DrawerNavLabel>Community Info</DrawerNavLabel>
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleNav('community-info')}
                          aria-expanded={expanded}
                          aria-label="Toggle Community Info sub-menu"
                          className="drawer-caret-btn"
                        >
                          <CaretIcon className="drawer-caret" />
                        </button>
                      </div>
                      <div className="drawer-subnav">
                        <button
                          type="button"
                          onClick={() => { closeMenu(); setCommunityInfoOpen(true); }}
                          aria-haspopup="dialog"
                          className="drawer-subnav-link font-heading"
                        >
                          <DrawerNavLabel>Hours & Info</DrawerNavLabel>
                        </button>
                        {communityInfoLinks.map((child) => {
                          const childActive = normalizePath(child.href) === currentPath;
                          return (
                            <a
                              key={child.href}
                              href={child.href}
                              onClick={closeMenu}
                              aria-current={childActive ? 'page' : undefined}
                              className={`drawer-subnav-link font-heading ${childActive ? 'is-active' : ''}`}
                            >
                              <DrawerNavLabel>{child.label}</DrawerNavLabel>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                const link = entry.link;
                const active = normalizePath(link.href) === currentPath;
                const hasChildren = !!link.children && link.children.length > 0;
                if (hasChildren) {
                  const expanded = expandedNav === link.href;
                  // Sub-nav is always rendered so it can open on hover (CSS);
                  // the `is-open` class mirrors the click/touch toggle state.
                  // Where the drawer is tap-driven, tapping the page name
                  // expands the group instead of navigating — the page itself is
                  // the first row of the sub-nav, so it stays one tap away. With
                  // a hover pointer the group expands on hover, so there the
                  // label navigates as usual.
                  return (
                    <div key={link.href} className={`drawer-nav-group ${expanded ? 'is-open' : ''}`}>
                      <div className="drawer-nav-row">
                        <a
                          href={link.href}
                          onClick={(e) => {
                            if (usesTapNav()) {
                              e.preventDefault();
                              toggleNav(link.href);
                              return;
                            }
                            closeMenu();
                          }}
                          aria-current={active ? 'page' : undefined}
                          className={`drawer-nav-link font-heading ${active ? 'is-active' : ''}`}
                        >
                          <DrawerNavLabel>{link.label}</DrawerNavLabel>
                        </a>
                        <button
                          type="button"
                          onClick={() => toggleNav(link.href)}
                          aria-expanded={expanded}
                          aria-label={`Toggle ${link.label} sub-menu`}
                          className="drawer-caret-btn"
                        >
                          <CaretIcon className="drawer-caret" />
                        </button>
                      </div>
                      <div className="drawer-subnav">
                        {/* Same rows as the desktop dropdown: parent page first,
                            then each child — keeps hamburger and top-nav in lockstep. */}
                        {pageDropdownLinks(link).map((item) => {
                          const itemActive = normalizePath(item.href) === currentPath;
                          return (
                            <a
                              key={item.href}
                              href={item.href}
                              onClick={closeMenu}
                              aria-current={itemActive ? 'page' : undefined}
                              className={`drawer-subnav-link font-heading ${itemActive ? 'is-active' : ''}`}
                            >
                              <DrawerNavLabel>{item.label}</DrawerNavLabel>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={closeMenu}
                    aria-current={active ? 'page' : undefined}
                    className={`drawer-nav-link font-heading ${active ? 'is-active' : ''}`}
                  >
                    <DrawerNavLabel>{link.label}</DrawerNavLabel>
                  </a>
                );
              })}
              {/* Schedule a Tour / Apply Now / Resident Access — plain rows */}
              <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                <a
                  href={scheduleTourLink.url || '/schedule-a-tour'}
                  target={scheduleTourLink._target || '_self'}
                  onClick={closeMenu}
                  className="drawer-nav-link font-heading"
                >
                  <DrawerNavLabel>{scheduleTourLink.label || 'Schedule a Tour'}</DrawerNavLabel>
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  onClick={closeMenu}
                  className="drawer-nav-link font-heading"
                >
                  <DrawerNavLabel>{applyNowLink.label || 'Apply Now'}</DrawerNavLabel>
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessHref || '#'}
                  target={residentAccessLink._target || '_blank'}
                  rel={residentAccessLink._target === '_self' ? undefined : 'noopener noreferrer'}
                  onClick={closeMenu}
                  className="drawer-nav-link font-heading"
                >
                  <DrawerNavLabel>{residentAccessLink.label || 'Resident Access'}</DrawerNavLabel>
                </a>
              </EditableSlotGroup>
              {faqNavLink ? (
                <a
                  href={faqNavLink.href}
                  onClick={closeMenu}
                  aria-current={normalizePath(faqNavLink.href) === currentPath ? 'page' : undefined}
                  className={`drawer-nav-link font-heading ${normalizePath(faqNavLink.href) === currentPath ? 'is-active' : ''}`}
                >
                  <DrawerNavLabel>{faqNavLink.label}</DrawerNavLabel>
                </a>
              ) : null}
            </nav>

            {/* ---- Visit Us / Let's Talk — side-rail drawer only ---- */}
            <div className="mt-10 hidden md:block">
              <h3 className="drawer-section-title font-heading">Visit Us</h3>
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                  className="drawer-text-link block font-body text-sm md:text-base"
                >
                  <span className="whitespace-pre-line">{addressLink.address}</span>
                </a>
              </EditableSlotGroup>
            </div>

            <div className="mt-8 hidden md:block">
              <h3 className="drawer-section-title font-heading">Let&apos;s Talk</h3>
              <a
                href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref drawer-text-link block font-body text-sm md:text-base"
              >
                <EditableToken
                  path="header.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                  as="span"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
              {drawerSocialIcons && (
                <div className="mt-4 flex items-center justify-start gap-5">
                  {drawerSocialIcons}
                </div>
              )}
            </div>

            {/* Bottom cluster: social on phones; credits in the desktop rail. On
                phones this pins social to the foot of the full-width overlay.
                The tablet rail renders neither, so its top padding is dropped
                there to avoid a band of empty space at the drawer's foot. */}
            <div className="mt-auto flex flex-col pt-12 md:pt-0 lg:pt-12">
              {drawerSocialIcons && (
                <div className="flex items-center justify-center gap-5 md:hidden">
                  {drawerSocialIcons}
                </div>
              )}

              {/* Credits — Managed-By / Powered-By / Designed-By (desktop rail
                  only; the narrower tablet rail omits them).
                  Every column is label-on-top + a logo band of the SAME height,
                  so items-start keeps all three labels on one line no matter
                  how tall a mark is — grow the band, never a single logo past
                  it. Each mark hangs from the TOP of its band (items-start) so
                  the shorter wordmarks sit under their own label instead of
                  drifting to the band's floor; pt-2 on those two backs them off
                  the label again without touching the labels' shared top edge.
                  Heights inside the band are deliberately unequal
                  (52 / 20 / 24), mirroring the footer's 44 / 28 / 32: the
                  management company's stacked lockup needs the full band to
                  stay legible, while the Multihub and Repli wordmarks read as
                  the same visual weight smaller. flex-wrap is a safety net —
                  an unusually wide company mark drops to its own line instead
                  of overflowing the fixed-width drawer. */}
              <div className="mt-8 mb-4 hidden flex-wrap items-start gap-x-6 gap-y-5 lg:flex">
                {managedByImage && (
                  <a
                    href={companyUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-w-0 flex-col opacity-90 hover:opacity-100 transition-opacity"
                  >
                    <span className="block whitespace-nowrap font-body text-[9px] leading-none tracking-[0.12em] opacity-70 mb-1.5">Professionally Managed By</span>
                    <span className="flex h-[52px] items-start">
                      <VibesitesImage
                        url={managedByImage}
                        alt={companyName || 'Property Management Company'}
                        height={52}
                        sizes="200px"
                        className="h-[52px] w-auto max-w-[168px] object-contain"
                        style={managedByNeedsWhitening ? { filter: 'brightness(0) invert(1)' } : undefined}
                      />
                    </span>
                  </a>
                )}
                <a
                  href="https://www.multihub.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-w-0 flex-col opacity-90 hover:opacity-100 transition-opacity"
                >
                  <span className="block whitespace-nowrap font-body text-[9px] leading-none tracking-[0.12em] opacity-70 mb-1.5">Website Powered By</span>
                  <span className="flex h-[52px] items-start pt-2">
                    <MultihubLogoMonoWhite height={20} alt="Multihub" />
                  </span>
                </a>
                <a
                  href="https://www.repli360.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-w-0 flex-col opacity-90 hover:opacity-100 transition-opacity"
                >
                  <span className="block whitespace-nowrap font-body text-[9px] leading-none tracking-[0.12em] opacity-70 mb-1.5">Website Designed By</span>
                  <span className="flex h-[52px] items-start pt-2">
                    <VibesitesImage
                      url={repliWhiteLogoUrl}
                      alt="Repli Logo"
                      height={24}
                      sizes="80px"
                      className="h-6 w-auto"
                    />
                  </span>
                </a>
              </div>
            </div>
          </div>
          </aside>
        </div>
      </HtmlPortal>

      <CommunityInfoModal
        open={communityInfoOpen}
        onClose={closeCommunityInfo}
        phone={phone}
        formatPhone={formatPhone}
        address={addressLink.address}
        addressUrl={addressLink.url || propertyAddressUrl}
        addressTarget={addressLink._target}
        hours={modalHours}
        applyNowUrl={applyNowLink.url || '/apply-now'}
        applyNowTarget={applyNowLink._target}
      />

      <style>{`
        /* Pinned white header bar */
        .header-bar {
          border-bottom: 1px solid var(--vibe-color-border);
        }
        /* Soft drop shadow under the nav bar so it separates from page content. */
        .header-elevated {
          box-shadow:
            0 1px 3px rgba(0,0,0,0.05),
            0 4px 14px rgba(0,0,0,0.04);
        }
        /* Fallback before nav height is measured — matches h-20 / lg:h-24
           plus the live offer-banner clearance so first paint can't leave
           content under the fixed header when a banner is present. */
        .header-spacer {
          height: calc(5rem + var(--offer-banner-height, 0px));
        }
        @media (min-width: 1024px) {
          .header-spacer {
            height: calc(6rem + var(--offer-banner-height, 0px));
          }
        }

        /* ---- Inline primary navigation (desktop) ---- */
        .top-nav-item {
          position: relative;
        }
        .top-nav-link {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.5rem 0.7rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.9375rem;
          font-weight: 400;
          line-height: 1;
          white-space: nowrap;
          color: var(--vibe-color-text);
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, opacity 0.2s ease;
        }
        .top-nav-link:hover,
        .top-nav-link.is-active,
        .top-nav-item:hover .top-nav-link,
        .top-nav-item:focus-within .top-nav-link {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        .top-nav-caret {
          transition: transform 0.2s ease;
        }
        .top-nav-item:hover .top-nav-caret,
        .top-nav-item:focus-within .top-nav-caret {
          transform: rotate(180deg);
        }

        /* Dropdown for pages that have sub-pages */
        .top-nav-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          min-width: 180px;
          padding: 0;
          background: var(--vibe-color-background);
          border: 0;
          border-radius: 0;
          overflow: hidden;
          box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
          opacity: 0;
          visibility: hidden;
          transform: translateY(6px);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
          z-index: 60;
        }
        .top-nav-item:hover .top-nav-dropdown,
        .top-nav-item:focus-within .top-nav-dropdown {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
        /* A row inside the dropdown was just clicked: keep the panel closed
           even though the pointer is still over the item. Cleared on mouse
           leave, so hovering the item again reopens it normally. */
        .top-nav-item.is-dismissed:hover .top-nav-dropdown,
        .top-nav-item.is-dismissed:focus-within .top-nav-dropdown {
          opacity: 0;
          visibility: hidden;
          transform: translateY(6px);
        }
        .top-nav-item.is-dismissed:hover .top-nav-caret,
        .top-nav-item.is-dismissed:focus-within .top-nav-caret {
          transform: none;
        }
        .top-nav-dropdown-link {
          display: block;
          padding: 1.25rem 1.25rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.9375rem;
          font-weight: 400;
          white-space: nowrap;
          color: var(--vibe-color-text);
          transition: background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease;
        }
        .top-nav-dropdown-link:hover,
        .top-nav-dropdown-link.is-active {
          background: #000;
          color: #fff;
        }
        /* Button variant (e.g. "Hours & Info") sitting among the anchor links —
           reset native button chrome so it matches the dropdown rows exactly. */
        .top-nav-dropdown-button {
          width: 100%;
          text-align: left;
          background: transparent;
          border: 0;
          cursor: pointer;
        }

        /* Menu toggle (hamburger ⇄ X) in the header bar. When the drawer is
           open it becomes a filled dark square with a white X and nudges up a
           little so it reads higher in the bar. */
        .menu-toggle {
          color: var(--vibe-color-text);
          border: 1px solid transparent;
          border-radius: 2px;
          transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
        }
        .menu-toggle.is-open {
          background: #111111;
          color: #ffffff;
          border-color: #111111;
          transform: translateY(-0.5rem);
        }

        /* Open-menu header: wherever the drawer is the side rail the bar stays
           white and intact (the page is merely pushed aside); the phone
           top-overlay drops the logo, compacts to a close-only strip, and blends
           into the dark drawer so page links can sit higher. */
        header.is-menu-open {
          background-color: var(--vibe-color-background);
        }
        @media (max-width: 767px) {
          header.is-menu-open {
            background-color: #111111;
          }
          header.is-menu-open .header-bar {
            border-bottom-color: transparent;
          }
          header.is-menu-open .header-bar > div {
            height: 3.5rem;
          }
          header.is-menu-open .header-logo-slot,
          header.is-menu-open .header-phone-icon {
            display: none;
          }
          header.is-menu-open .menu-toggle.is-open {
            background: transparent;
            border-color: transparent;
            color: #ffffff;
            transform: none;
          }
        }

        /* While the drawer is open, collapse the SpecialOfferBanner and drop
           its reserved height so the header rises to the top instead of leaving
           a band where the banner was. The stylesheet !important beats the
           inline --offer-banner-height / max-height the banner sets in JS. */
        html.vibe-nav-open {
          --offer-banner-height: 0px !important;
        }
        html.vibe-nav-open .sob {
          max-height: 0 !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }

        /* ---- Drawer panel ----
           Phone: full-width, slides down from the top.
           Tablet (md+): narrow left rail, slides in from the left; desktop
           (lg+) widens that same rail. The width lives on a custom property
           because <body>'s push transform reads it too. */
        :root {
          ${DRAWER_WIDTH_VAR}: ${TABLET_DRAWER_WIDTH};
        }
        @media (min-width: 1024px) {
          :root {
            ${DRAWER_WIDTH_VAR}: ${DESKTOP_DRAWER_WIDTH};
          }
        }
        .drawer-panel {
          left: 0;
          right: 0;
          width: 100%;
          background: #111111;
          color: #ffffff;
          transform: translateY(-100%);
          transition: transform 0.3s ease-out;
        }
        .drawer-panel.is-open {
          transform: translateY(0);
        }
        .drawer-panel-inner {
          padding-top: var(--drawer-pad-top, 4rem);
        }
        @media ${SIDE_DRAWER_MQ} {
          .drawer-panel {
            right: auto;
            width: ${DRAWER_WIDTH};
            transform: translateX(-100%);
          }
          .drawer-panel.is-open {
            transform: translateX(0);
          }
          .drawer-panel-inner {
            padding-top: 2.25rem;
            padding-bottom: 2.25rem;
          }
        }

        /* Drawer nav links: large, light-weight heading rows.
           Phone overlay: centered. Side rail: left-aligned.
           Hover / active bump to full opacity; boldness snaps on via overlay,
           then slides away to the left on leave. */
        .drawer-nav-link {
          display: block;
          width: fit-content;
          padding: 0.5rem 0;
          font-size: 1.5rem;
          font-weight: 300;
          line-height: 1.15;
          color: #ffffff;
          background: transparent;
          border: 0;
          cursor: pointer;
          text-align: center;
          text-decoration: none;
          opacity: 0.85;
          transition: opacity 0.2s ease;
        }
        @media ${SIDE_DRAWER_MQ} {
          .drawer-nav-link {
            text-align: left;
            padding: 0.95rem 0;
          }
        }
        .drawer-nav-link:hover,
        .drawer-nav-link.is-active {
          opacity: 1;
        }

        .drawer-nav-label {
          position: relative;
          display: inline-block;
          white-space: nowrap;
        }
        /* Invisible bold copy reserves the wider hover width so the overlay
           never clips the right edge of heavier glyphs. */
        .drawer-nav-label-measure {
          display: inline-block;
          font-weight: 700;
          visibility: hidden;
          white-space: nowrap;
        }
        .drawer-nav-label-face {
          position: absolute;
          left: 0;
          top: 0;
          font-weight: inherit;
          white-space: nowrap;
        }
        /* Bold overlay sits on the drawer background so it masks the light
           text underneath (avoids a doubled/ghosted glyph). Snaps on instantly,
           then width eases to 0 on leave so boldness slides away left. */
        .drawer-nav-label::after {
          content: attr(data-text);
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          font-weight: 700;
          color: #ffffff;
          background-color: #111111;
          white-space: nowrap;
          overflow: hidden;
          width: 0;
          pointer-events: none;
          transition: width 0.35s ease;
        }
        .drawer-nav-link:hover .drawer-nav-label-face,
        .drawer-nav-link.is-active .drawer-nav-label-face,
        .drawer-subnav-link:hover .drawer-nav-label-face,
        .drawer-subnav-link.is-active .drawer-nav-label-face {
          color: transparent;
        }
        .drawer-nav-link:hover .drawer-nav-label::after,
        .drawer-nav-link.is-active .drawer-nav-label::after,
        .drawer-subnav-link:hover .drawer-nav-label::after,
        .drawer-subnav-link.is-active .drawer-nav-label::after {
          width: 100%;
          transition: none;
        }

        /* A nav row that pairs a link with its expand/collapse caret.
           Phone overlay: 3-column grid keeps the label on the true center line.
           Side rail: simple flex row, left-aligned like the original drawer. */
        .drawer-nav-group {
          width: 100%;
        }
        .drawer-nav-row {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          column-gap: 0.5rem;
        }
        .drawer-nav-row .drawer-nav-link {
          grid-column: 2;
          justify-self: center;
        }
        .drawer-caret-btn {
          grid-column: 3;
          justify-self: start;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          color: #ffffff;
          background: transparent;
          border: 0;
          cursor: pointer;
          flex-shrink: 0;
        }
        @media ${SIDE_DRAWER_MQ} {
          .drawer-nav-row {
            display: flex;
            column-gap: 0.75rem;
            gap: 0.75rem;
          }
          .drawer-nav-row .drawer-nav-link {
            grid-column: auto;
            justify-self: auto;
          }
          .drawer-caret-btn {
            grid-column: auto;
            justify-self: auto;
          }
        }
        .drawer-caret {
          width: 1.1rem;
          height: 1.1rem;
          transition: transform 0.25s ease;
        }
        /* Caret flips when the group is toggled open. Hover auto-expand is
           limited to real pointers — on touch, sticky :hover would leave menus
           open (this is the CSS half of usesTapNav()). */
        .drawer-nav-group.is-open .drawer-caret {
          transform: rotate(180deg);
        }
        @media ${HOVER_NAV_MQ} {
          .drawer-nav-group:hover .drawer-caret {
            transform: rotate(180deg);
          }
        }

        /* Sub-nav under a parent row. Always in the DOM; it collapses to zero
           height and expands when toggled open (click / touch via .is-open).
           Phone overlay: centered. Side rail: indented left rail. Hover
           auto-expand is pointer-only. */
        .drawer-subnav {
          display: flex;
          flex-direction: column;
          align-items: center;
          max-height: 0;
          overflow: hidden;
          opacity: 0;
          margin: 0;
          transition: max-height 0.3s ease, opacity 0.25s ease, margin 0.3s ease;
        }
        .drawer-nav-group.is-open .drawer-subnav {
          max-height: 30rem;
          opacity: 1;
          margin: 0.25rem 0 0.5rem;
        }
        @media ${SIDE_DRAWER_MQ} {
          .drawer-subnav {
            align-items: flex-start;
            padding-left: 1rem;
            border-left: 1px solid rgba(255, 255, 255, 0.2);
          }
        }
        @media ${HOVER_NAV_MQ} {
          .drawer-nav-group:hover .drawer-subnav {
            max-height: 30rem;
            opacity: 1;
            margin: 0.25rem 0 0.5rem;
          }
        }
        .drawer-subnav-link {
          display: block;
          width: fit-content;
          padding: 0.4rem 0;
          font-size: 1.05rem;
          font-weight: 300;
          color: rgba(255, 255, 255, 0.85);
          background: transparent;
          border: 0;
          cursor: pointer;
          text-align: center;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        @media ${SIDE_DRAWER_MQ} {
          .drawer-subnav-link {
            text-align: left;
          }
        }
        .drawer-subnav-link:hover,
        .drawer-subnav-link.is-active {
          color: #ffffff;
        }

        /* "Visit Us" / "Let's Talk" section labels. */
        .drawer-section-title {
          font-size: 1.05rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.4rem;
        }

        /* Drawer address / phone links */
        .drawer-text-link {
          color: rgba(255, 255, 255, 0.85);
          transition: color 0.2s ease;
        }
        .drawer-text-link:hover { color: #ffffff; }

        /* Drawer social icons */
        .drawer-social {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.5rem;
          height: 1.5rem;
          color: #ffffff;
          opacity: 0.85;
          transition: opacity 0.2s ease;
        }
        .drawer-social:hover { opacity: 1; }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Expose the Interactive Map page in the navigation when this field has a value. Defaults to custom_engrain_sightmap_url; Engrain ID and ApartmentSync also unlock the page.',
      defaultField: 'custom_engrain_sightmap_url',
      suggestedFields: [
        'custom_engrain_sightmap_url',
        'custom_engrain_sightmap_id',
        'custom_engrain_sitemap_url',
      ],
      defaultValue: '',
    },
    'header.fee_guide_cta_label': {
      label: 'Community Info: Apply Now CTA Label',
      type: 'text',
      description:
        'Always shown by default next to Contact Our Team. Links to /apply-now. Default "Apply Now". Blank to hide.',
      defaultValue: 'Apply Now',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule a Tour Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/schedule-a-tour',
          suggestedFields: ['property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/apply-now',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          suggestedFields: ['property_resident_portal_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
